// Libs
import classNames from 'classnames/bind';
import { Box, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseLabelValueField, BaseMap, Loading } from '~/components/common';
// Others
import { getContactDetail } from '~/thunks/contact/contactThunk';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, EMPTY_STRING } from '~/utils/constants/common';
import { googleMapApiKey } from '~/utils/constants/env';
import { getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IContactDetail } from '~/utils/interface/contact';
// Styles, images, icons
import styles from './ContactInformation.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ContactInformation = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { id: contactId } = useParams();
  const { t } = useTranslation(['admin/contact']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [contactDetail, setContactDetail] = useState<IContactDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetContactDetail = useCallback(
    (contactId: string) => {
      setIsLoading(true);

      dispatch(getContactDetail(contactId))
        .unwrap()
        .then((res) => {
          const { data } = res;
          if (!data) return;

          setContactDetail(data);
        })
        .catch((err) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!contactId) return;

    handleGetContactDetail(contactId);
  }, [contactId, handleGetContactDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box component={'div'} id='contactInformationPage' className={cx('container')}>
      <Grid2 container spacing={'24px'}>
        <Grid2 size={6}>
          <BaseLabelValueField
            label={t('tab_information_name')}
            value={getFullName({ firstName: contactDetail?.firstName, lastName: contactDetail?.lastName })}
          />

          <BaseLabelValueField
            label={t('tab_information_email')}
            value={contactDetail?.email || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_phone')}
            value={contactDetail?.phoneNumber || EMPTY_STRING}
          />
        </Grid2>

        <Grid2 container direction={'column'} size={6} spacing={0}>
          <Stack className={cx('mapContainer')}>
            <BaseLabelValueField
              label={t('tab_information_address')}
              value={contactDetail?.address}
              bordered={false}
              labelSize={'auto'}
            />

            <Stack flex={1} className={cx('map')}>
              {isLoaded && (
                <BaseMap
                  {...(contactDetail?.latitude &&
                    contactDetail?.longitude && {
                      position: { lat: contactDetail?.latitude, lng: contactDetail?.longitude },
                    })}
                />
              )}
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}
    </Box>
  );
};

export default ContactInformation;
